import { useState } from 'react';
import { useProvince } from '../contexts/ProvinceContext';
import { useKingdomPolitics } from '../hooks/useKingdomPolitics';
import { useAppShellPlatform } from '../hooks/useAppShellPlatform';
import { MobilePoliticsShell } from '../components/politics/MobilePoliticsShell';
import { DesktopPoliticsShell } from '../components/politics/DesktopPoliticsShell';
import { Spinner } from '../components/Spinner';
import { ErrorMessage } from '../components/ErrorMessage';
import { useToast } from '../hooks/useToast';
import { useAuth } from '../contexts/AuthContext';
import { castVote } from '../api/politics';
import type { PoliticsStandingsResponse } from '../types/politics';

/**
 * SF-152: named, fully-typed props shared by MobilePoliticsShell/DesktopPoliticsShell — same
 * ExploreShellProps/MagicShellProps precedent (SF-139/SF-148). State stays hoisted here (not
 * per-shell) so a mid-vote breakpoint flip doesn't lose in-flight UI state.
 */
export interface PoliticsShellProps {
  standings: PoliticsStandingsResponse;
  myProvinceId: string | null;
  isVoting: boolean;
  handleVote: (targetProvinceId: string) => void;
  handleRetract: () => void;
}

/**
 * Kingdom Politics page — monarch election standings and voting.
 *
 * Always scoped to the player's own kingdom. You can only vote within your kingdom.
 */
export function Politics() {
  const { province } = useProvince();
  const { worldId } = useAuth();
  const { addToast } = useToast();
  const platform = useAppShellPlatform();

  // Canonical format is "kingdom:island" (per CLAUDE.md + KingdomId.toKeyString)
  const kingdomId = province?.metadata?.location
    ? `${province.metadata.location.kingdom}:${province.metadata.location.island}`
    : null;

  const [isVoting, setIsVoting] = useState(false);
  const [overrideStandings, setOverrideStandings] = useState<PoliticsStandingsResponse | null>(null);

  const { data: fetchedData, isLoading, error, refresh } = useKingdomPolitics(kingdomId);
  const standings = overrideStandings ?? fetchedData;

  const myProvinceId = province?.metadata?.id ?? null;

  const handleVote = async (targetProvinceId: string) => {
    if (!worldId || !myProvinceId) return;
    setIsVoting(true);
    try {
      const result = await castVote(worldId, myProvinceId, targetProvinceId);
      setOverrideStandings(result.standings);
      // Updates useKingdomPolitics' shared module cache (30s TTL) — without this, KingdomHub.tsx
      // (a separate mounted instance of the same hook) can show the pre-vote monarch/standings
      // for up to 30s. Politics.tsx's own display is already correct via overrideStandings above;
      // this call is purely to keep the OTHER consumer's cache fresh.
      refresh();
      if (result.monarchChanged) {
        const name = result.standings.monarchProvinceName ?? 'Unknown';
        addToast(`${name} is now Monarch!`, 'success');
      } else {
        addToast('Vote recorded', 'success');
      }
    } catch (err: unknown) {
      addToast(err instanceof Error ? err.message : 'Failed to cast vote', 'failure');
    } finally {
      setIsVoting(false);
    }
  };

  const handleRetract = async () => {
    if (!worldId || !myProvinceId) return;
    setIsVoting(true);
    try {
      const result = await castVote(worldId, myProvinceId, null);
      setOverrideStandings(result.standings);
      refresh();
      addToast('Vote retracted', 'success');
    } catch (err: unknown) {
      addToast(err instanceof Error ? err.message : 'Failed to retract vote', 'failure');
    } finally {
      setIsVoting(false);
    }
  };

  if (isLoading && !standings) {
    return <Spinner />;
  }

  if (error && !standings) {
    return <ErrorMessage message={error} />;
  }

  if (!standings) {
    return <div />;
  }

  const shellProps: PoliticsShellProps = {
    standings,
    myProvinceId,
    isVoting,
    handleVote,
    handleRetract,
  };

  return platform === 'mobile'
    ? <MobilePoliticsShell {...shellProps} />
    : <DesktopPoliticsShell {...shellProps} />;
}
